import {
  CalendarX,
  CircleCheck,
  Clock,
  Lock,
  LockOpen,
  RotateCcw,
  TriangleAlert,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { StatusTone } from '@shared/design-system/ui'
import { formatFileSize } from '@shared/lib/format/file-size'
import type { MembersCompleteness, MembersAreaDetail } from './members-area.contract'
import type {
  AccessStatus,
  AccessTimelineKind,
  ContentStatus,
  EnrollmentOrigin,
  MembersContentKind,
  RevocationReason,
} from './members-area.types'

type Presentation = { label: string; tone: StatusTone; icon: LucideIcon }

const accessStatusPresentations: Record<AccessStatus, Presentation> = {
  ACTIVE: { label: 'Ativo', tone: 'success', icon: CircleCheck },
  PAST_DUE: { label: 'Pagamento em atraso', tone: 'awaiting', icon: TriangleAlert },
  EXPIRED: { label: 'Expirado', tone: 'neutral', icon: CalendarX },
  REVOKED: { label: 'Revogado', tone: 'closed', icon: Lock },
}

export const accessStatusOrder: AccessStatus[] = ['ACTIVE', 'PAST_DUE', 'EXPIRED', 'REVOKED']

export function getAccessStatusPresentation(status: AccessStatus): Presentation {
  return accessStatusPresentations[status]
}

const contentStatusPresentations: Record<ContentStatus, Presentation> = {
  PUBLISHED: { label: 'Publicado', tone: 'success', icon: CircleCheck },
  DRAFT: { label: 'Rascunho', tone: 'neutral', icon: Clock },
}

export function getContentStatusPresentation(status: ContentStatus): Presentation {
  return contentStatusPresentations[status]
}

export const contentKindLabels: Record<MembersContentKind, string> = {
  course: 'Curso',
  files: 'Arquivos',
}

const completenessPresentations: Record<MembersCompleteness, Presentation> = {
  empty: { label: 'Incompleto', tone: 'awaiting', icon: TriangleAlert },
  incomplete: { label: 'Incompleto', tone: 'awaiting', icon: TriangleAlert },
  ready: { label: 'Rascunho', tone: 'neutral', icon: Clock },
  published: { label: 'Publicado', tone: 'success', icon: CircleCheck },
}

export function getCompletenessPresentation(completeness: MembersCompleteness): Presentation {
  return completenessPresentations[completeness]
}

export function contentEditorActionLabel(completeness: MembersCompleteness): string {
  return completeness === 'empty' || completeness === 'incomplete'
    ? 'Completar cadastro'
    : 'Editar conteúdo'
}

export function memberPreviewPath(productId: string, lessonId?: string) {
  const base = `/area-membros/preview/${encodeURIComponent(productId)}`
  return lessonId === undefined ? base : `${base}/aulas/${encodeURIComponent(lessonId)}`
}

export function publishBlockReason(
  detail: Pick<MembersAreaDetail, 'kind' | 'modules' | 'assets' | 'coverImage'>,
): string | null {
  if (detail.kind === 'course') {
    const hasLesson = detail.modules.some((module) => module.lessons.length > 0)
    if (!hasLesson) return 'Adicione pelo menos um módulo com uma aula para publicar.'
    const videoPending = detail.modules.some((module) =>
      module.lessons.some((lesson) => lesson.kind === 'video' && lesson.video?.status !== 'READY'),
    )
    if (videoPending) return 'Toda aula de vídeo precisa de um vídeo pronto para publicar.'
  } else {
    const readyFiles = detail.assets.filter(
      (asset) => asset.status === 'READY' && asset.lessonId === null,
    )
    if (readyFiles.length === 0) return 'Envie pelo menos um arquivo para publicar.'
  }
  if (!detail.coverImage.trim()) {
    return 'Adicione a capa do produto. Ela aparece no card da área de membros.'
  }
  return null
}

export function formatVideoDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const rest = String(total % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`
}

export { formatFileSize }

export function titleFromVideoFile(file: File) {
  const base = file.name.replace(/\.[^.]+$/, '').trim()
  return base || 'Nova aula'
}

export const enrollmentOriginLabels: Record<EnrollmentOrigin, string> = {
  SALE: 'Venda',
  ORDER_BUMP: 'Order bump',
  UPSELL: 'Upsell',
  SUBSCRIPTION: 'Assinatura',
  MANUAL: 'Manual',
}

export const revocationReasonLabels: Record<RevocationReason, string> = {
  REFUND: 'Reembolso',
  CHARGEBACK: 'Chargeback',
  SUBSCRIPTION_CANCELED: 'Assinatura cancelada',
  MANUAL: 'Revogado manualmente',
}

const timelinePresentations: Record<AccessTimelineKind, { tone: StatusTone; icon: LucideIcon }> = {
  GRANTED: { tone: 'success', icon: LockOpen },
  RESTORED: { tone: 'success', icon: RotateCcw },
  PAST_DUE: { tone: 'awaiting', icon: TriangleAlert },
  EXPIRED: { tone: 'neutral', icon: CalendarX },
  REVOKED: { tone: 'closed', icon: Lock },
}

export function getTimelinePresentation(kind: AccessTimelineKind) {
  return timelinePresentations[kind]
}

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'America/Sao_Paulo',
})

const dateTimeFormatter = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Sao_Paulo',
})

export function formatMembersDate(iso: string): string {
  return dateFormatter.format(new Date(iso))
}

export function formatMembersDateTime(iso: string): string {
  return dateTimeFormatter.format(new Date(iso))
}
